import { useState } from "react";

import apiClient from "../../../services/apiClient";
import Modal from "../../../components/Modal";
import StepProgressBar from "../../../components/StepProgressBar";
import CloseButton from "../../../components/CloseButton";
import ReviewSettingsPanel from "../../../components/ReviewSettingsPanel";
import { useDefaultRewardPoints } from "../../reward/RewardPointsField";
import DateSelectionPage from "./WorksheetModal/DateSelectionPage/DateSelectionPage";
import StudentSelectionPage from "./WorksheetModal/StudentSelectionPage/StudentSelectionPage";
import MathReviewTypeField from "./MathReviewTypeField";
import "../../../styles/WorksheetModalV2.css";
import "./MathBankAssignModal.css";

// 수학 문제은행 출제 설정 창(2026-11-03). 문제 고르기는 문제뱅크 탭 화면(MathBankPage) 안에서 하고,
// 여기서는 제목 · 복습 · 마감일 · 학생만 고른다. 출제가 끝나면 결과 화면 없이 바로 닫는다(대표: "그냥 출제하면 닫아").
// 확정하면 수학 사이트에 학습지 문서가 생기고 우리 과제가 나간다(서버 api/assignment/math_bank.py).
// 복습은 틀린 문제를 쌍둥이(같은 원본의 다른 변형)부터 닮은 문제로 다시 내는 뺑뺑이라 자동 · 무제한이 기본이다(math_bank_review.py).
// 상세 해설은 만들지 않는다(2026-11-02 대표). 서버도 detail 을 늘 false 로 보낸다.
const STEP_LIST = [
  { key: "title", label: "제목" },
  { key: "retry", label: "복습" },
  { key: "date", label: "마감일" },
  { key: "student", label: "학생" },
];

// 처음 풀 때 풀이 흔적을 사진으로 받는다(서버 api/assignment/math_work_photo.py). 학습지 출제 창과 같은 값
const WORK_PHOTO_OPTION_LIST = [
  { value: "required", label: "필수" },
  { value: "optional", label: "선택" },
  { value: "", label: "안 받음" },
];

// 서버는 날짜만 받는다. toISOString 은 UTC 라 밤 9시 뒤에 고르면 하루 당겨진다
const toLocalDateText = (date) =>
  `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;

const MathBankAssignModal = ({ problemList, defaultTitle = "", onClose, onAssigned }) => {
  const [currentStep, setCurrentStep] = useState("title");
  const [isBusy, setIsBusy] = useState(false);
  const [title, setTitle] = useState(defaultTitle);
  const [selectedDate, setSelectedDate] = useState(() => {
    const today = new Date();
    today.setHours(23, 59, 59, 999);
    return today;
  });
  const [hideAfterClosed, setHideAfterClosed] = useState(true);
  const [selectedStudents, setSelectedStudents] = useState([]);
  const [rewardPoints, setRewardPoints] = useState(100);
  const [rewardMinScore, setRewardMinScore] = useState(0);
  const [isRetryEnabled, setIsRetryEnabled] = useState(true);
  const [isAutoRetryEnabled, setIsAutoRetryEnabled] = useState(true);
  const [maxRetries, setMaxRetries] = useState(-1);
  const [workPhotoPolicy, setWorkPhotoPolicy] = useState("optional");
  // 쌍둥이 복습 난이도(2026-11-03 대표). 문제은행 출제는 늘 쌍둥이로 복습한다
  const [reviewProblem, setReviewProblem] = useState("twin");
  const [reviewDifficulty, setReviewDifficulty] = useState("auto");
  useDefaultRewardPoints("math_type_worksheet", setRewardPoints, setRewardMinScore);

  const submit = async () => {
    if (isBusy) return;
    setIsBusy(true);
    try {
      const response = await apiClient.post(
        "/api/v1/assignment/math/bank/create",
        {
          problem_ids: problemList.map((one) => one.problem_id),
          student_ids: selectedStudents.map((student) => student.id),
          title,
          due_date: toLocalDateText(selectedDate),
          reward_points: rewardPoints,
          reward_min_score: rewardMinScore,
          is_retry_enabled: isRetryEnabled,
          is_auto_retry_enabled: isAutoRetryEnabled,
          max_retries: maxRetries,
          hide_after_closed: hideAfterClosed,
          work_photo_policy: workPhotoPolicy,
          review_problem: reviewProblem,
          review_difficulty: reviewDifficulty,
        },
        { withAuth: true }
      );
      alert(`${response.data.assignment_count}명에게 출제했어요.`);
      onAssigned();
    } catch (error) {
      alert(error?.response?.data?.error || "과제 출제에 실패했습니다. 다시 시도해주세요.");
    } finally {
      setIsBusy(false);
    }
  };

  const goPrev = () => {
    const stepIndex = STEP_LIST.findIndex((step) => step.key === currentStep);
    if (stepIndex > 0) setCurrentStep(STEP_LIST[stepIndex - 1].key);
    else onClose();
  };
  const handleStepClick = (stepKey) => {
    const targetIndex = STEP_LIST.findIndex((step) => step.key === stepKey);
    const currentIndex = STEP_LIST.findIndex((step) => step.key === currentStep);
    if (targetIndex < currentIndex) setCurrentStep(stepKey);
  };

  const renderTitle = () => {
    const goNext = () => {
      if (!title.trim()) return alert("과제 제목을 입력해주세요.");
      setCurrentStep("retry");
    };
    return (
      <div className="math-bank-modal__step">
        <CloseButton onClick={onClose} />
        <div className="math-bank-modal__scroll">
          <div className="ws-modal-title math-bank-modal__heading">과제 제목</div>
          <input
            type="text"
            className="math-bank-modal__title-input"
            placeholder="과제 제목을 입력해주세요."
            value={title}
            onChange={(event) => setTitle(event.target.value)}
            onKeyDown={(event) => event.key === "Enter" && goNext()}
            autoFocus
          />
        </div>
        <div className="ws-bottom-bar math-bank-modal__bottom">
          <button className="ws-btn-next" onClick={goNext}>
            다음
          </button>
        </div>
      </div>
    );
  };

  const renderRetry = () => (
    <div className="math-bank-modal__step">
      <CloseButton onClick={onClose} />
      <div className="math-bank-modal__scroll">
        <div className="ws-modal-title math-bank-modal__heading">복습 설정</div>
        <ReviewSettingsPanel
          isRetryEnabled={isRetryEnabled}
          onToggleRetry={() => setIsRetryEnabled(!isRetryEnabled)}
          isAutoRetryEnabled={isAutoRetryEnabled}
          onSetAutoRetry={setIsAutoRetryEnabled}
          maxRetries={maxRetries}
          onSetMaxRetries={setMaxRetries}
        >
          <MathReviewTypeField
            reviewProblem={reviewProblem}
            onSetReviewProblem={setReviewProblem}
            reviewDifficulty={reviewDifficulty}
            onSetReviewDifficulty={setReviewDifficulty}
          />
        </ReviewSettingsPanel>
        <div className="math-bank-modal__field math-bank-modal__field--spaced">
          <div className="math-bank-modal__label">풀이 사진</div>
          <div className="math-bank-modal__chip-list">
            {WORK_PHOTO_OPTION_LIST.map((one) => (
              <button
                key={one.value}
                type="button"
                className={`ws-filter-chip${one.value === workPhotoPolicy ? " active" : ""}`}
                onClick={() => setWorkPhotoPolicy(one.value)}
              >
                {one.label}
              </button>
            ))}
          </div>
        </div>
      </div>
      <div className="ws-bottom-bar math-bank-modal__bottom">
        <button className="ws-btn-prev" onClick={goPrev}>
          이전
        </button>
        <button className="ws-btn-next" onClick={() => setCurrentStep("date")}>
          다음
        </button>
      </div>
    </div>
  );

  const renderContent = () => {
    if (currentStep === "title") return renderTitle();
    if (currentStep === "retry") return renderRetry();
    if (currentStep === "date") {
      return (
        <DateSelectionPage
          onClose={onClose}
          onClickPrev={goPrev}
          onClickNext={() => setCurrentStep("student")}
          selectedDate={selectedDate}
          handleDateSelect={setSelectedDate}
          hideAfterClosed={hideAfterClosed}
          setHideAfterClosed={setHideAfterClosed}
          rewardPoints={rewardPoints}
          setRewardPoints={setRewardPoints}
          rewardMinScore={rewardMinScore}
          setRewardMinScore={setRewardMinScore}
        />
      );
    }
    return (
      <StudentSelectionPage
        onClose={onClose}
        onClickPrev={goPrev}
        handleAddStudents={submit}
        selectedStudents={selectedStudents}
        setSelectedStudents={setSelectedStudents}
        selectedDate={selectedDate}
      />
    );
  };

  return (
    <Modal>
      <div className="math-bank-modal">
        <StepProgressBar steps={STEP_LIST} currentStep={currentStep} onStepClick={handleStepClick} />
        {renderContent()}
      </div>
    </Modal>
  );
};

export default MathBankAssignModal;
